type Light = (x: number, y: number, radius: number, strength: number) => void;
type Pointer = { x: number; y: number } | null;
const between = (min: number, max: number) => min + Math.random() * (max - min);

/** Each creature keeps its own proportions, rhythm, and elastic tentacle offsets. */
export function createJellyfishLight() {
  const creatures = [1.08, 0.7, 0.43, 0.88, 0.56].map((scale, index) => ({
    scale: scale * between(0.9, 1.1),
    lane: [0.16, 0.8, 0.52, 0.34, 0.91][index]!,
    phase: between(0, Math.PI * 2),
    tempo: between(0.85, 1.65),
    crown: between(0.5, 0.86),
    drift: between(0.065, 0.14),
    brightness: 0.6 + scale * 0.35,
    strands: Array.from({ length: Math.round(between(5, 8)) }, () => ({
      length: between(1.35, 2.7),
      wave: between(3, 6),
      phase: between(0, Math.PI * 2),
      // x/y displacement and velocity for each point, retained between frames.
      motion: new Float32Array(31 * 4),
    })),
  }));
  let previousTime = 0;

  return (width: number, height: number, time: number, light: Light, pointer: Pointer) => {
    const dt = Math.min(Math.max(time - previousTime, 0), 0.1);
    previousTime = time;
    const count = width < 700 ? 2 : width < 1100 ? 4 : 5;
    for (let index = 0; index < count; index++) {
      const jelly = creatures[index]!;
      const phase = time * jelly.tempo + jelly.phase;
      const pulse = (1 + Math.sin(phase)) / 2;
      const size = Math.min(width * 0.17, height * 0.14, 112) * jelly.scale;
      const radius = size * (0.86 + pulse * 0.14);
      const crown = size * (jelly.crown + (1 - pulse) * 0.12);
      const travel = height + size * 3.2;
      const x = width * jelly.lane + Math.sin(time * jelly.drift + jelly.phase) * size * 0.85;
      // Each contraction gives a small forward stroke instead of a constant glide.
      const stroke = Math.sin(phase) * size * 0.035;
      // Evenly stagger the passages so departures cannot empty the desktop.
      // A small individual drift preserves the spacing without synchronized strokes.
      const passage =
        time / 130 + index / count + 0.16 + Math.sin(time * jelly.drift + jelly.phase) * 0.012;
      const y = height + size * 0.4 - (passage % 1) * travel - stroke;
      const tilt = Math.sin(time * jelly.drift * 1.4 + jelly.phase) * 0.23;
      const cs = Math.cos(tilt),
        sn = Math.sin(tilt);
      const point: Light = (px, py, spread, strength) =>
        light(x + px * cs - py * sn, y + px * sn + py * cs, spread, strength * jelly.brightness);
      const softness = 0.65 + jelly.scale * 0.35;

      point(0, -crown * 0.35, size * 1.25, 0.24);
      for (let row = -crown; row <= 0; row += 10) {
        const span = radius * Math.sqrt(Math.max(0, 1 - (row / crown) ** 2));
        for (let col = -span; col <= span; col += 10) point(col, row, 18 * softness, 0.24);
      }
      for (let step = 0; step <= 40; step++) {
        const angle = (Math.PI * step) / 40;
        point(Math.cos(angle) * radius, -Math.sin(angle) * crown, 21 * softness, 0.95);
        const u = step / 20 - 1;
        point(u * radius, (1 - u * u) * size * 0.14, 18 * softness, 0.85);
      }
      for (const rib of [-0.55, 0, 0.55]) {
        for (let step = 0; step <= 16; step++) {
          const t = step / 16;
          point(rib * radius * Math.sin((t * Math.PI) / 2), -crown * (1 - t), 16 * softness, 0.5);
        }
      }

      for (const [strand, tentacle] of jelly.strands.entries()) {
        const root = (strand * 2) / (jelly.strands.length - 1) - 1;
        for (let step = 0; step <= 30; step++) {
          const t = step / 30;
          const sway =
            Math.sin(time * jelly.tempo * 0.8 - t * tentacle.wave + tentacle.phase) *
            size *
            0.23 *
            t;
          const px = root * radius * (0.8 - 0.3 * t) + sway;
          const py = size * 0.08 + t * size * tentacle.length;
          const wx = x + px * cs - py * sn,
            wy = y + px * sn + py * cs;
          let targetX = 0,
            targetY = 0;
          if (pointer) {
            const dx = wx - pointer.x,
              dy = wy - pointer.y;
            const distance = Math.hypot(dx, dy);
            const reach = 75 + size * 0.3;
            const push = Math.max(0, 1 - distance / reach) ** 2 * 80 * t;
            targetX = (distance > 0.01 ? dx / distance : root || 1) * push;
            targetY = (distance > 0.01 ? dy / distance : 0) * push;
          }
          const offset = step * 4,
            motion = tentacle.motion;
          // Small integration steps keep the damped spring stable after a slow frame.
          const steps = Math.max(1, Math.ceil(dt * 60)),
            h = dt / steps;
          for (let tick = 0; tick < steps; tick++) {
            motion[offset + 2]! +=
              ((targetX - motion[offset]!) * 55 - motion[offset + 2]! * 12) * h;
            motion[offset + 3]! +=
              ((targetY - motion[offset + 1]!) * 55 - motion[offset + 3]! * 12) * h;
            motion[offset]! += motion[offset + 2]! * h;
            motion[offset + 1]! += motion[offset + 3]! * h;
          }
          light(
            wx + motion[offset]!,
            wy + motion[offset + 1]!,
            (14 + (1 - t) * 5) * softness,
            (0.8 + pulse * 0.15) * (1 - t * 0.8) * jelly.brightness,
          );
        }
      }
    }
  };
}
